import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Crown, Star, Stamp, CreditCard, Gift, Loader2,
  TrendingUp, History, Trophy, Coins, Percent, Heart, HeartOff,
  ShoppingBag, Clock, CheckCircle2, XCircle, ChefHat, Truck, MapPin,
  Plus, Trash2, Home, Building, RotateCcw, User, Edit2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const fmt = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

// ── Types ──
interface LoyaltyProgram { id: string; type: "points" | "stamp" | "cashback"; name: string; description: string | null; points_per_real: number | null; points_to_redeem: number | null; reward_value: number | null; stamps_required: number | null; stamp_reward_description: string | null; cashback_percentage: number | null; }
interface CustomerLoyalty { id: string; loyalty_program_id: string; points_balance: number; stamps_count: number; cashback_balance: number; total_orders: number; total_spent: number; }
interface LoyaltyTransaction { id: string; loyalty_program_id: string; type: string; amount: number; description: string | null; created_at: string; order_id: string | null; }
interface Order { id: string; status: string; order_type: string; subtotal: number; delivery_fee: number; discount: number; total: number; payment_method: string | null; created_at: string; table_number: number | null; notes: string | null; restaurant_id: string; }
interface OrderItem { id: string; order_id: string; product_id: string | null; product_name: string; quantity: number; unit_price: number; total_price: number; }
interface Address { id: string; label: string; street: string; number: string | null; complement: string | null; neighborhood: string | null; city: string; state: string; zip_code: string | null; is_default: boolean; }
interface Favorite { id: string; product_id: string; product_name?: string; product_price?: number; product_image?: string | null; }

type TabKey = "resumo" | "pedidos" | "favoritos" | "enderecos" | "historico";

const MinhaConta = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const restaurantSlug = searchParams.get("r");

  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [restaurant, setRestaurant] = useState<any>(null);
  const [programs, setPrograms] = useState<LoyaltyProgram[]>([]);
  const [loyalty, setLoyalty] = useState<CustomerLoyalty[]>([]);
  const [transactions, setTransactions] = useState<LoyaltyTransaction[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>("resumo");
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [reordering, setReordering] = useState<string | null>(null);

  // Address form state
  const [addrLabel, setAddrLabel] = useState("Casa");
  const [addrStreet, setAddrStreet] = useState("");
  const [addrNumber, setAddrNumber] = useState("");
  const [addrComplement, setAddrComplement] = useState("");
  const [addrNeighborhood, setAddrNeighborhood] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [addrState, setAddrState] = useState("");
  const [addrZip, setAddrZip] = useState("");
  const [addrDefault, setAddrDefault] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate(`/menu${restaurantSlug ? `?r=${restaurantSlug}` : ""}`); return; }
      setCurrentUser(user);

      let query = supabase.from("restaurants").select("*").eq("is_active", true);
      if (restaurantSlug) query = query.eq("slug", restaurantSlug);
      const { data: rest } = await query.limit(1).maybeSingle();
      if (!rest) { setLoading(false); return; }
      setRestaurant(rest);

      const [progRes, loyaltyRes, txRes, ordersRes, addrRes, favRes] = await Promise.all([
        supabase.from("loyalty_programs").select("*").eq("restaurant_id", rest.id).eq("is_active", true),
        supabase.from("customer_loyalty").select("*").eq("customer_id", user.id).eq("restaurant_id", rest.id),
        supabase.from("loyalty_transactions").select("*").eq("customer_id", user.id).eq("restaurant_id", rest.id).order("created_at", { ascending: false }).limit(50),
        supabase.from("orders").select("*").eq("customer_id", user.id).eq("restaurant_id", rest.id).order("created_at", { ascending: false }).limit(50),
        supabase.from("customer_addresses").select("*").eq("customer_id", user.id).eq("restaurant_id", rest.id).order("is_default", { ascending: false }),
        supabase.from("customer_favorites").select("*, products(name, price, image_url)").eq("customer_id", user.id).eq("restaurant_id", rest.id),
      ]);

      setPrograms((progRes.data as any[]) || []);
      setLoyalty((loyaltyRes.data as any[]) || []);
      setTransactions((txRes.data as any[]) || []);
      const ordersData = (ordersRes.data as any[]) || [];
      setOrders(ordersData);
      setAddresses((addrRes.data as any[]) || []);

      // Map favorites with product info
      const favData = (favRes.data as any[]) || [];
      setFavorites(favData.map((f: any) => ({
        id: f.id,
        product_id: f.product_id,
        product_name: f.products?.name || "Produto",
        product_price: f.products?.price || 0,
        product_image: f.products?.image_url || null,
      })));

      if (ordersData.length > 0) {
        const orderIds = ordersData.map((o: any) => o.id);
        const { data: items } = await supabase.from("order_items").select("*").in("order_id", orderIds);
        setOrderItems((items as any[]) || []);
      }

      setLoading(false);
    };
    init();
  }, [restaurantSlug, navigate]);

  const getLoyaltyForProgram = (programId: string) => loyalty.find(l => l.loyalty_program_id === programId);
  const getItemsForOrder = (orderId: string) => orderItems.filter(i => i.order_id === orderId);
  const primaryColor = restaurant?.primary_color || "#f97316";

  const statusConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
    received: { label: "Recebido", icon: <Clock className="h-3.5 w-3.5" />, color: "#3b82f6" },
    preparing: { label: "Em preparo", icon: <ChefHat className="h-3.5 w-3.5" />, color: "#f59e0b" },
    ready: { label: "Pronto", icon: <CheckCircle2 className="h-3.5 w-3.5" />, color: "#10b981" },
    out_for_delivery: { label: "Saiu p/ entrega", icon: <Truck className="h-3.5 w-3.5" />, color: "#8b5cf6" },
    completed: { label: "Finalizado", icon: <CheckCircle2 className="h-3.5 w-3.5" />, color: "#22c55e" },
    cancelled: { label: "Cancelado", icon: <XCircle className="h-3.5 w-3.5" />, color: "#ef4444" },
  };

  const orderTypeLabels: Record<string, string> = { dine_in: "No local", takeout: "Retirada", delivery: "Entrega", scheduled: "Agendado" };
  const paymentLabels: Record<string, string> = { pix: "PIX", credit_card: "Crédito", debit_card: "Débito", cash: "Dinheiro", mercadopago: "MercadoPago" };

  // ── Reorder ──
  const handleReorder = async (order: Order) => {
    setReordering(order.id);
    try {
      const items = getItemsForOrder(order.id);
      if (items.length === 0) { toast.error("Não foi possível carregar os itens do pedido."); return; }
      // Store cart items in sessionStorage and redirect to menu
      const cartItems = items.map(item => ({
        cartId: crypto.randomUUID(),
        productId: item.product_id || "",
        name: item.product_name,
        price: Number(item.unit_price),
        qty: item.quantity,
        addons: [],
      }));
      sessionStorage.setItem("viamenu_reorder_cart", JSON.stringify(cartItems));
      toast.success("Itens adicionados ao carrinho!");
      navigate(`/menu${restaurantSlug ? `?r=${restaurantSlug}` : ""}`);
    } catch {
      toast.error("Erro ao reordenar.");
    } finally {
      setReordering(null);
    }
  };

  // ── Addresses CRUD ──
  const resetAddressForm = () => {
    setAddrLabel("Casa"); setAddrStreet(""); setAddrNumber(""); setAddrComplement("");
    setAddrNeighborhood(""); setAddrCity(""); setAddrState(""); setAddrZip(""); setAddrDefault(false);
    setEditingAddress(null);
  };

  const openAddressEdit = (addr: Address) => {
    setEditingAddress(addr);
    setAddrLabel(addr.label); setAddrStreet(addr.street); setAddrNumber(addr.number || "");
    setAddrComplement(addr.complement || ""); setAddrNeighborhood(addr.neighborhood || "");
    setAddrCity(addr.city); setAddrState(addr.state); setAddrZip(addr.zip_code || "");
    setAddrDefault(addr.is_default);
    setShowAddressModal(true);
  };

  const saveAddress = async () => {
    if (!addrStreet || !addrCity || !addrState) { toast.error("Preencha rua, cidade e estado."); return; }
    setSavingAddress(true);
    try {
      // If setting as default, unset others
      if (addrDefault && currentUser && restaurant) {
        await supabase.from("customer_addresses").update({ is_default: false } as any)
          .eq("customer_id", currentUser.id).eq("restaurant_id", restaurant.id);
      }

      const payload: any = {
        customer_id: currentUser.id,
        restaurant_id: restaurant.id,
        label: addrLabel,
        street: addrStreet,
        number: addrNumber || null,
        complement: addrComplement || null,
        neighborhood: addrNeighborhood || null,
        city: addrCity,
        state: addrState,
        zip_code: addrZip || null,
        is_default: addrDefault,
      };

      if (editingAddress) {
        const { error } = await supabase.from("customer_addresses").update(payload).eq("id", editingAddress.id);
        if (error) throw error;
        setAddresses(prev => prev.map(a => a.id === editingAddress.id ? { ...a, ...payload } : addrDefault ? { ...a, is_default: false } : a));
        toast.success("Endereço atualizado!");
      } else {
        const { data, error } = await supabase.from("customer_addresses").insert(payload).select().single();
        if (error) throw error;
        setAddresses(prev => addrDefault ? [...prev.map(a => ({ ...a, is_default: false })), data as any] : [...prev, data as any]);
        toast.success("Endereço salvo!");
      }
      setShowAddressModal(false);
      resetAddressForm();
    } catch (e: any) {
      toast.error(e.message || "Erro ao salvar endereço.");
    } finally {
      setSavingAddress(false);
    }
  };

  const deleteAddress = async (id: string) => {
    const { error } = await supabase.from("customer_addresses").delete().eq("id", id);
    if (error) { toast.error("Erro ao remover."); return; }
    setAddresses(prev => prev.filter(a => a.id !== id));
    toast.success("Endereço removido.");
  };

  // ── Favorites ──
  const removeFavorite = async (favId: string) => {
    const { error } = await supabase.from("customer_favorites").delete().eq("id", favId);
    if (error) { toast.error("Erro ao remover favorito."); return; }
    setFavorites(prev => prev.filter(f => f.id !== favId));
    toast.success("Favorito removido.");
  };

  const programIcon = (type: string) => {
    if (type === "points") return <Coins className="h-5 w-5" />;
    if (type === "stamp") return <Stamp className="h-5 w-5" />;
    return <Percent className="h-5 w-5" />;
  };
  const programLabel = (type: string) => {
    if (type === "points") return "Pontos";
    if (type === "stamp") return "Carimbos";
    return "Cashback";
  };

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: primaryColor }} />
    </div>
  );
  if (!currentUser) return null;
  if (!restaurant) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8 text-center">
      <div>
        <h2 className="font-bold text-xl mb-2 text-foreground">Restaurante não encontrado</h2>
        <p className="text-muted-foreground">Verifique o link e tente novamente.</p>
      </div>
    </div>
  );

  const totalOrders = orders.length || loyalty.reduce((s, l) => s + l.total_orders, 0);
  const totalSpent = loyalty.reduce((s, l) => s + Number(l.total_spent), 0);

  const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
    { key: "resumo", label: "Resumo", icon: <Crown className="h-4 w-4" /> },
    { key: "pedidos", label: "Pedidos", icon: <ShoppingBag className="h-4 w-4" /> },
    { key: "favoritos", label: "Favoritos", icon: <Heart className="h-4 w-4" /> },
    { key: "enderecos", label: "Endereços", icon: <MapPin className="h-4 w-4" /> },
    { key: "historico", label: "Fidelidade", icon: <History className="h-4 w-4" /> },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-lg mx-auto flex items-center gap-3 p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/menu${restaurantSlug ? `?r=${restaurantSlug}` : ""}`)} className="shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-lg text-foreground">Minha Conta</h1>
            <p className="text-xs text-muted-foreground">{restaurant.name}</p>
          </div>
          <User className="h-6 w-6" style={{ color: primaryColor }} />
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Summary cards */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-3 gap-3">
          <Card className="border-border">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-foreground">{totalOrders}</p>
              <p className="text-[10px] text-muted-foreground">Pedidos</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-foreground">{favorites.length}</p>
              <p className="text-[10px] text-muted-foreground">Favoritos</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-foreground">{addresses.length}</p>
              <p className="text-[10px] text-muted-foreground">Endereços</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-0.5 overflow-x-auto scrollbar-hide border-b border-border pb-1">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-shrink-0 py-2 px-3 text-xs font-medium rounded-t-lg transition-colors flex items-center gap-1 ${
                activeTab === tab.key ? "border-b-2 text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
              style={activeTab === tab.key ? { borderColor: primaryColor, color: primaryColor } : {}}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ RESUMO TAB ═══ */}
        {activeTab === "resumo" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            {programs.length === 0 ? (
              <Card className="border-border">
                <CardContent className="p-6 text-center text-muted-foreground">
                  <Gift className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">Nenhum programa de fidelidade ativo</p>
                  <p className="text-sm mt-1">Este restaurante ainda não configurou recompensas.</p>
                </CardContent>
              </Card>
            ) : programs.map(prog => {
              const cl = getLoyaltyForProgram(prog.id);
              return (
                <motion.div key={prog.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <Card className="border-border overflow-hidden">
                    <div className="h-1" style={{ background: primaryColor }} />
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base text-foreground">
                        {programIcon(prog.type)} {prog.name}
                        <Badge variant="secondary" className="ml-auto text-xs">{programLabel(prog.type)}</Badge>
                      </CardTitle>
                      {prog.description && <p className="text-xs text-muted-foreground">{prog.description}</p>}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {prog.type === "points" && (
                        <>
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-3xl font-bold" style={{ color: primaryColor }}>{Math.floor(Number(cl?.points_balance || 0))}</p>
                              <p className="text-xs text-muted-foreground">pontos acumulados</p>
                            </div>
                            <div className="text-right text-sm text-muted-foreground">
                              <p>Resgate: {prog.points_to_redeem || 100} pts</p>
                              <p className="font-medium text-foreground">= {fmt(Number(prog.reward_value || 10))}</p>
                            </div>
                          </div>
                          {cl && prog.points_to_redeem && (
                            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (Number(cl.points_balance) / prog.points_to_redeem) * 100)}%`, background: primaryColor }} />
                            </div>
                          )}
                        </>
                      )}
                      {prog.type === "stamp" && (
                        <>
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-3xl font-bold" style={{ color: primaryColor }}>{cl?.stamps_count || 0}</p>
                              <p className="text-xs text-muted-foreground">de {prog.stamps_required || 10} carimbos</p>
                            </div>
                            <div className="text-right">
                              <Gift className="h-5 w-5 text-muted-foreground inline-block mb-1" />
                              <p className="text-sm font-medium text-foreground">{prog.stamp_reward_description || "Produto grátis"}</p>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-wrap">
                            {Array.from({ length: prog.stamps_required || 10 }).map((_, i) => (
                              <div key={i} className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs transition-all"
                                style={{ borderColor: primaryColor, background: i < (cl?.stamps_count || 0) ? primaryColor : "transparent", color: i < (cl?.stamps_count || 0) ? "white" : primaryColor }}>
                                {i < (cl?.stamps_count || 0) ? <Star className="h-4 w-4" /> : i + 1}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      {prog.type === "cashback" && (
                        <div className="flex items-end justify-between">
                          <div>
                            <p className="text-3xl font-bold" style={{ color: primaryColor }}>{fmt(Number(cl?.cashback_balance || 0))}</p>
                            <p className="text-xs text-muted-foreground">saldo disponível</p>
                          </div>
                          <div className="text-right text-sm text-muted-foreground">
                            <p className="font-medium text-foreground">{prog.cashback_percentage || 5}%</p>
                            <p>de volta por pedido</p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* ═══ PEDIDOS TAB ═══ */}
        {activeTab === "pedidos" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            {orders.length === 0 ? (
              <Card className="border-border">
                <CardContent className="p-6 text-center text-muted-foreground">
                  <ShoppingBag className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">Nenhum pedido encontrado</p>
                  <p className="text-sm mt-1">Faça seu primeiro pedido pelo cardápio!</p>
                </CardContent>
              </Card>
            ) : orders.map((order, idx) => {
              const status = statusConfig[order.status] || statusConfig.received;
              const items = getItemsForOrder(order.id);
              const isExpanded = expandedOrder === order.id;
              const dateStr = new Date(order.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

              return (
                <motion.div key={order.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
                  <Card className="border-border overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => setExpandedOrder(isExpanded ? null : order.id)}>
                    <CardContent className="p-0">
                      <div className="p-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${status.color}15`, color: status.color }}>
                          {status.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-foreground">#{order.id.slice(0, 6).toUpperCase()}</p>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-none font-medium" style={{ background: `${status.color}15`, color: status.color }}>
                              {status.label}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {dateStr} • {orderTypeLabels[order.order_type] || order.order_type}
                            {order.table_number ? ` • Mesa ${order.table_number}` : ""}
                          </p>
                        </div>
                        <p className="text-sm font-bold text-foreground shrink-0">{fmt(Number(order.total))}</p>
                      </div>
                      <div className="px-3 pb-2">
                        <p className="text-xs text-muted-foreground truncate">
                          {items.map(i => `${i.quantity}x ${i.product_name}`).join(", ") || "Carregando itens..."}
                        </p>
                      </div>
                      {isExpanded && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="border-t border-border">
                          <div className="p-3 space-y-2">
                            <div className="space-y-1">
                              {items.map(item => (
                                <div key={item.id} className="flex justify-between text-sm">
                                  <span className="text-foreground">{item.quantity}x {item.product_name}</span>
                                  <span className="text-muted-foreground">{fmt(Number(item.total_price))}</span>
                                </div>
                              ))}
                            </div>
                            <div className="border-t border-border pt-2 space-y-1 text-sm">
                              <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{fmt(Number(order.subtotal))}</span></div>
                              {Number(order.delivery_fee) > 0 && <div className="flex justify-between text-muted-foreground"><span>Entrega</span><span>{fmt(Number(order.delivery_fee))}</span></div>}
                              {Number(order.discount) > 0 && <div className="flex justify-between text-green-600"><span>Desconto</span><span>-{fmt(Number(order.discount))}</span></div>}
                              <div className="flex justify-between font-bold text-foreground"><span>Total</span><span>{fmt(Number(order.total))}</span></div>
                            </div>
                            {order.payment_method && (
                              <div className="flex flex-wrap gap-2 pt-1">
                                <Badge variant="secondary" className="text-xs">💳 {paymentLabels[order.payment_method] || order.payment_method}</Badge>
                              </div>
                            )}
                            {order.notes && <p className="text-xs text-muted-foreground italic">📝 {order.notes}</p>}

                            {/* Reorder button */}
                            {(order.status === "completed" || order.status === "cancelled") && items.length > 0 && (
                              <Button
                                size="sm"
                                className="w-full mt-2 text-white"
                                style={{ backgroundColor: primaryColor }}
                                onClick={(e) => { e.stopPropagation(); handleReorder(order); }}
                                disabled={reordering === order.id}
                              >
                                {reordering === order.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                                Pedir novamente
                              </Button>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* ═══ FAVORITOS TAB ═══ */}
        {activeTab === "favoritos" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            {favorites.length === 0 ? (
              <Card className="border-border">
                <CardContent className="p-6 text-center text-muted-foreground">
                  <Heart className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">Nenhum favorito ainda</p>
                  <p className="text-sm mt-1">Toque no ❤️ nos produtos do cardápio para salvar aqui.</p>
                </CardContent>
              </Card>
            ) : favorites.map((fav, idx) => (
              <motion.div key={fav.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
                <Card className="border-border overflow-hidden">
                  <CardContent className="p-3 flex items-center gap-3">
                    {fav.product_image ? (
                      <img src={fav.product_image} alt={fav.product_name} className="w-14 h-14 rounded-xl object-cover shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center shrink-0">
                        <ShoppingBag className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{fav.product_name}</p>
                      <p className="text-sm font-bold mt-0.5" style={{ color: primaryColor }}>{fmt(Number(fav.product_price || 0))}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="shrink-0 text-destructive hover:text-destructive" onClick={() => removeFavorite(fav.id)}>
                      <HeartOff className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* ═══ ENDEREÇOS TAB ═══ */}
        {activeTab === "enderecos" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => { resetAddressForm(); setShowAddressModal(true); }} style={{ backgroundColor: primaryColor }} className="text-white">
                <Plus className="h-4 w-4 mr-1" /> Novo endereço
              </Button>
            </div>
            {addresses.length === 0 ? (
              <Card className="border-border">
                <CardContent className="p-6 text-center text-muted-foreground">
                  <MapPin className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">Nenhum endereço salvo</p>
                  <p className="text-sm mt-1">Salve seus endereços para agilizar futuros pedidos.</p>
                </CardContent>
              </Card>
            ) : addresses.map((addr, idx) => (
              <motion.div key={addr.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
                <Card className="border-border overflow-hidden">
                  <CardContent className="p-3 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${primaryColor}15`, color: primaryColor }}>
                      {addr.label === "Casa" ? <Home className="h-5 w-5" /> : addr.label === "Trabalho" ? <Building className="h-5 w-5" /> : <MapPin className="h-5 w-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground">{addr.label}</p>
                        {addr.is_default && <Badge variant="secondary" className="text-[10px]">Padrão</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {addr.street}{addr.number ? `, ${addr.number}` : ""}{addr.complement ? ` - ${addr.complement}` : ""}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {addr.neighborhood ? `${addr.neighborhood}, ` : ""}{addr.city} - {addr.state}
                        {addr.zip_code ? ` • ${addr.zip_code}` : ""}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openAddressEdit(addr)}>
                        <Edit2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => deleteAddress(addr.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* ═══ FIDELIDADE / HISTÓRICO TAB ═══ */}
        {activeTab === "historico" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
            {transactions.length === 0 ? (
              <Card className="border-border">
                <CardContent className="p-6 text-center text-muted-foreground">
                  <History className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">Nenhuma transação ainda</p>
                  <p className="text-sm mt-1">Faça pedidos para começar a acumular recompensas!</p>
                </CardContent>
              </Card>
            ) : transactions.map(tx => {
              const prog = programs.find(p => p.id === tx.loyalty_program_id);
              const isEarn = tx.type === "earn";
              return (
                <motion.div key={tx.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}>
                  <Card className="border-border">
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: isEarn ? `${primaryColor}20` : "#ef444420", color: isEarn ? primaryColor : "#ef4444" }}>
                        {isEarn ? <TrendingUp className="h-4 w-4" /> : <Gift className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{tx.description || (isEarn ? "Recompensa recebida" : "Resgate")}</p>
                        <p className="text-xs text-muted-foreground">
                          {prog ? programLabel(prog.type) : ""} • {new Date(tx.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <span className="text-sm font-bold shrink-0" style={{ color: isEarn ? primaryColor : "#ef4444" }}>
                        {isEarn ? "+" : "-"}{prog?.type === "cashback" ? fmt(Number(tx.amount)) : Math.floor(Number(tx.amount))}
                        {prog?.type === "points" ? " pts" : prog?.type === "stamp" ? " ✓" : ""}
                      </span>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Back to menu */}
        <div className="pt-4 pb-8">
          <Button variant="outline" className="w-full" onClick={() => navigate(`/menu${restaurantSlug ? `?r=${restaurantSlug}` : ""}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Voltar ao cardápio
          </Button>
        </div>
      </div>

      {/* ═══ ADDRESS MODAL ═══ */}
      <Dialog open={showAddressModal} onOpenChange={setShowAddressModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingAddress ? "Editar Endereço" : "Novo Endereço"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              {["Casa", "Trabalho", "Outro"].map(l => (
                <Button key={l} size="sm" variant={addrLabel === l ? "default" : "outline"} onClick={() => setAddrLabel(l)}
                  style={addrLabel === l ? { backgroundColor: primaryColor } : {}}>
                  {l === "Casa" ? <Home className="h-3.5 w-3.5 mr-1" /> : l === "Trabalho" ? <Building className="h-3.5 w-3.5 mr-1" /> : <MapPin className="h-3.5 w-3.5 mr-1" />}
                  {l}
                </Button>
              ))}
            </div>
            <div>
              <Label className="text-xs">Rua *</Label>
              <Input value={addrStreet} onChange={e => setAddrStreet(e.target.value)} placeholder="Rua, Avenida..." />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Número</Label><Input value={addrNumber} onChange={e => setAddrNumber(e.target.value)} placeholder="123" /></div>
              <div><Label className="text-xs">Complemento</Label><Input value={addrComplement} onChange={e => setAddrComplement(e.target.value)} placeholder="Apto, Bloco..." /></div>
            </div>
            <div><Label className="text-xs">Bairro</Label><Input value={addrNeighborhood} onChange={e => setAddrNeighborhood(e.target.value)} placeholder="Bairro" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><Label className="text-xs">Cidade *</Label><Input value={addrCity} onChange={e => setAddrCity(e.target.value)} placeholder="Cidade" /></div>
              <div><Label className="text-xs">UF *</Label><Input value={addrState} onChange={e => setAddrState(e.target.value)} placeholder="SP" maxLength={2} /></div>
            </div>
            <div><Label className="text-xs">CEP</Label><Input value={addrZip} onChange={e => setAddrZip(e.target.value)} placeholder="00000-000" /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={addrDefault} onChange={e => setAddrDefault(e.target.checked)} className="rounded" />
              Definir como endereço padrão
            </label>
            <Button className="w-full text-white" style={{ backgroundColor: primaryColor }} onClick={saveAddress} disabled={savingAddress}>
              {savingAddress ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingAddress ? "Atualizar" : "Salvar Endereço"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MinhaConta;
